import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPromotionsDataView, getShippingPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import {
    AvailabilityMessageType,
    BillToModel,
    CartLineModel,
    ShipToModel,
    WarehouseModel,
} from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { MGBW_CONFIGURATION_ITEM_NUMBER, MGBW_SHIPPING_AVAILABILITY } from "@tSurya/Common/MgbwPropertyKeys";
import { getVisibleMgbwSelections } from "@tSurya/Common/MgbwConfiguratorHelpers";
import {
    getPaymentBreakdown,
    getShippingPromotionTotal,
    getSplitTotals,
    hasMgbwTermsProperty,
    isTermsPaymentMethod,
    PaymentSplitChoice,
} from "@tSurya/Common/Utilities/paymentBreakdown";
import PrintBrandBadge from "@tSurya/Components/PrintBrandBadge";
import { getActiveThemeId, THEME_IDS, ThemeId } from "@tSurya/theme/themeHelper";
import { getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import React from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import styled from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    const cart = cartState.value;
    const promotionsDataView = getPromotionsDataView(state, state.pages.orderConfirmation.cartId);
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId).value,
        shipTo: getShipToState(state, cart?.shipToId).value,
        pickUpWarehouse: cart?.defaultWarehouse || state.context.session.pickUpWarehouse,
        order: getOrderState(state, cart?.orderNumber).value,
        shippingPromotions: promotionsDataView.value ? getShippingPromotions(promotionsDataView.value) : undefined,
        language: state.context.session.language,
        isUkWebsite: getIsUkWebsite(state),
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const getPersistedPaymentSplitChoice = (value: string | undefined): PaymentSplitChoice | undefined => {
    if (value === "prepayAll" || value === "prepayMgbwOnly" || value === "payAllOnTerms") {
        return value;
    }
    return undefined;
};

const getLineAvailability = (line: CartLineModel) => {
    const shippingAvailability = line.properties?.[MGBW_SHIPPING_AVAILABILITY] || "";
    if (shippingAvailability) {
        return shippingAvailability;
    }
    if (line.properties?.mgbw_isSwatch === "true") {
        return translate("In Stock");
    }
    return line.availability?.messageType !== AvailabilityMessageType.NoMessage ? line.availability?.message || "" : "";
};

const getLineProductName = (line: CartLineModel) => {
    const normalizedErpNumber = line.erpNumber.trim().toLowerCase();
    const parentProductName = line.parentProductErpNumber || "";
    const normalizedParentProductName = parentProductName.endsWith("_P")
        ? parentProductName.slice(0, -2)
        : parentProductName;
    const descriptiveName = [line.productName, normalizedParentProductName, line.shortDescription].find(
        value => value && value.trim().toLowerCase() !== normalizedErpNumber,
    );

    return descriptiveName || line.productName || line.shortDescription || line.erpNumber;
};

// Brand identity for the print header, selected from the active site/theme so the document
// shows the correct active brand in the nav and the matching centered wordmark.
interface BrandHeaderConfig {
    navLabel: string;
    wordmark: React.ReactNode;
}

const BRAND_HEADER_CONFIG: Record<ThemeId, BrandHeaderConfig> = {
    [THEME_IDS.SURYA]: {
        navLabel: "Surya",
        wordmark: "SURYA",
    },
    [THEME_IDS.GLOBAL_VIEWS]: {
        navLabel: "Global Views",
        wordmark: "GLOBAL VIEWS",
    },
    [THEME_IDS.MGBW]: {
        navLabel: "Mitchell Gold + Bob Williams",
        wordmark: (
            <>
                Mitchell Gold
                <br />
                +Bob Williams
            </>
        ),
    },
};

const BRAND_NAV_ORDER: ThemeId[] = [THEME_IDS.SURYA, THEME_IDS.GLOBAL_VIEWS, THEME_IDS.MGBW];

// Print-only layout. Rendered through a portal as a direct child of <body> and hidden on
// screen. In print, every other direct child of <body> is removed from layout via display:none
// so only this clean document is rendered (display:none avoids the blank trailing pages that
// the visibility:hidden trick produces because hidden elements still occupy layout space).
const PrintRoot = styled.div`
    display: none;

    @media print {
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        color: #000;
        font-family: "Times New Roman", Georgia, serif;
        padding: 0 24px;
        box-sizing: border-box;
    }
`;

const GlobalPrintHide = styled.div`
    display: none;
`;

const BrandNav = styled.div`
    display: flex;
    justify-content: center;
    gap: 28px;
    font-size: 12px;
    letter-spacing: 1px;
    color: #9b9b9b;
    text-transform: uppercase;
    margin-bottom: 18px;
`;

const ActiveBrand = styled.span`
    color: #333;
    font-weight: 600;
`;

const Wordmark = styled.div`
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.1;
    color: #1a1a1a;
    margin-bottom: 24px;
`;

const DocTitle = styled.div`
    text-align: center;
    font-size: 30px;
    margin: 8px 0 18px;
    color: #222;
`;

const MetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    font-size: 12px;
    border-bottom: 1px solid #cfcfcf;
    padding-bottom: 12px;
    margin-bottom: 12px;
`;

const MetaBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const MetaItem = styled.div`
    display: flex;
    gap: 6px;
`;

const MetaLabel = styled.span`
    color: #333;
    font-weight: 600;
`;

const MetaValue = styled.span`
    color: #555;
`;

const InfoGrid = styled.div`
    display: flex;
    gap: 24px;
    font-size: 12px;
    border-bottom: 1px solid #cfcfcf;
    padding-bottom: 12px;
    margin-bottom: 12px;
`;

const InfoColumn = styled.div`
    flex: 1 1 0;
`;

const InfoTitle = styled.div`
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    color: #444;
    font-weight: 700;
    margin-bottom: 6px;
`;

const InfoLine = styled.div`
    color: #555;
    line-height: 1.5;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

const Th = styled.th<{ align?: string }>`
    text-align: ${({ align }) => align || "left"};
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    color: #444;
    border-bottom: 1px solid #cfcfcf;
    padding: 8px 6px;
    font-weight: 600;
`;

const Td = styled.td<{ align?: string }>`
    text-align: ${({ align }) => align || "left"};
    vertical-align: top;
    padding: 14px 6px;
    border-bottom: 1px solid #e6e6e6;
`;

const ItemCell = styled.div`
    display: flex;
    gap: 18px;
`;

const ProductMedia = styled.div`
    width: 96px;
    flex: 0 0 96px;

    > div {
        margin-top: 6px;
    }
`;

const ProductImg = styled.img`
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
`;

const ItemName = styled.div`
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 8px;
`;

const SpecRow = styled.div`
    display: flex;
    gap: 8px;
    font-size: 11px;
    line-height: 1.5;
`;

const SpecLabel = styled.span`
    color: #333;
    min-width: 72px;
`;

const SpecValue = styled.span`
    color: #555;
`;

const ProductSize = styled.div`
    color: #555;
    font-size: 11px;
    line-height: 1.5;
    margin-top: 2px;
`;

const ServiceLabel = styled.div`
    font-style: italic;
    color: #545454;
    font-size: 11px;
    margin-top: 6px;
`;

const TotalsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
`;

const Totals = styled.div`
    width: 260px;
    font-size: 12px;
`;

const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 6px;
`;

const GrandTotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 13px;
    padding: 12px 6px;
    border-top: 2px solid #333;
    margin-top: 4px;
`;

const PaymentAllocation = styled.div`
    border-top: 1px solid #cfcfcf;
    margin: 12px 6px 0;
    padding-top: 8px;
`;

const PaymentAllocationRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
`;

const AddressBlock: React.FC<{
    companyName?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city?: string;
    stateAbbreviation?: string;
    postalCode?: string;
}> = ({ companyName, address1, address2, address3, address4, city, stateAbbreviation, postalCode }) => {
    const cityStateZip = [city, stateAbbreviation].filter(Boolean).join(", ");
    const lastLine = [cityStateZip, postalCode].filter(Boolean).join(" ");
    return (
        <>
            {companyName && <InfoLine>{companyName}</InfoLine>}
            {address1 && <InfoLine>{address1}</InfoLine>}
            {address2 && <InfoLine>{address2}</InfoLine>}
            {address3 && <InfoLine>{address3}</InfoLine>}
            {address4 && <InfoLine>{address4}</InfoLine>}
            {lastLine && <InfoLine>{lastLine}</InfoLine>}
        </>
    );
};

const PrintOrderConfirmationView: React.FC<Props> = ({
    cart,
    billTo,
    shipTo,
    pickUpWarehouse,
    order,
    shippingPromotions,
    language,
    isUkWebsite,
}) => {
    if (!cart) {
        return null;
    }

    if (typeof document === "undefined") {
        return null;
    }

    const activeThemeId = getActiveThemeId();
    const orderNumber = cart.erpOrderNumber || cart.orderNumber;
    const orderDate = order?.orderDate || cart.orderDate;
    const statusDisplay = order?.statusDisplay || cart.statusDisplay;
    const isPickUp = cart.fulfillmentMethod === FulfillmentMethod.PickUp;
    const carrierDescription = cart.carrier?.description;
    const serviceDescription = cart.shipVia?.description;
    const ukShippingMethod = isUkWebsite
        ? siteMessage("CheckoutShipping_OrderConfirmation_ShipMethod_DisplayText")
        : undefined;
    const lines: CartLineModel[] = cart.cartLines || [];
    const paymentMethods = cart.paymentOptions?.paymentMethods;
    const persistedPaymentSplitChoice = getPersistedPaymentSplitChoice(cart.properties?.paymentSplitChoice);
    const userHasTerms =
        persistedPaymentSplitChoice === "prepayMgbwOnly" ||
        persistedPaymentSplitChoice === "payAllOnTerms" ||
        !!paymentMethods?.some(isTermsPaymentMethod);
    const userHasMgbwTerms =
        persistedPaymentSplitChoice === "payAllOnTerms" || hasMgbwTermsProperty(billTo?.properties);
    const paymentBreakdown = getPaymentBreakdown(
        cart,
        userHasTerms,
        userHasMgbwTerms,
        getShippingPromotionTotal(shippingPromotions),
    );
    const effectiveSplitChoice =
        persistedPaymentSplitChoice && paymentBreakdown.availableChoices.includes(persistedPaymentSplitChoice)
            ? persistedPaymentSplitChoice
            : paymentBreakdown.defaultChoice;
    const splitTotals = getSplitTotals(paymentBreakdown, effectiveSplitChoice);
    const whiteGloveTotal = paymentBreakdown.mgbw.whiteGlove + paymentBreakdown.suryaGv.whiteGlove;
    const baseShippingAndHandling = Math.max((cart.shippingAndHandling || 0) - whiteGloveTotal, 0);
    const termsMethod = paymentMethods?.find(isTermsPaymentMethod);
    const termsLabel = termsMethod?.description || termsMethod?.name;
    const showPaymentAllocation = paymentBreakdown.scenario !== "allCreditCard";

    const renderAddress = (model?: BillToModel | ShipToModel | null) => {
        if (!model) {
            return null;
        }
        return (
            <AddressBlock
                companyName={model.companyName}
                address1={model.address1}
                address2={model.address2}
                address3={model.address3}
                address4={model.address4}
                city={model.city}
                stateAbbreviation={model.state?.abbreviation}
                postalCode={model.postalCode}
            />
        );
    };

    const renderWarehouseAddress = (model?: WarehouseModel | null) => {
        if (!model) {
            return null;
        }
        return (
            <AddressBlock
                companyName={model.description || model.name}
                address1={model.address1}
                address2={model.address2}
                city={model.city}
                stateAbbreviation={model.state}
                postalCode={model.postalCode}
            />
        );
    };

    const content = (
        <>
            <GlobalPrintHide
                dangerouslySetInnerHTML={{
                    __html: `<style>
                        @media print {
                            body > *:not(#sur-print-orderconfirmation-root) { display: none !important; }
                        }
                    </style>`,
                }}
            />
            <PrintRoot id="sur-print-orderconfirmation-root">
                {isUkWebsite ? (
                    <Wordmark>{translate("Surya UK")}</Wordmark>
                ) : (
                    <>
                        <BrandNav>
                            {BRAND_NAV_ORDER.map(themeId => {
                                const config = BRAND_HEADER_CONFIG[themeId];
                                return themeId === activeThemeId ? (
                                    <ActiveBrand key={themeId}>{translate(config.navLabel)}</ActiveBrand>
                                ) : (
                                    <span key={themeId}>{translate(config.navLabel)}</span>
                                );
                            })}
                        </BrandNav>
                        <Wordmark>{BRAND_HEADER_CONFIG[activeThemeId].wordmark}</Wordmark>
                    </>
                )}
                <DocTitle>
                    {translate("Order")} #{orderNumber}
                </DocTitle>

                <MetaRow>
                    <MetaBlock>
                        {orderDate && (
                            <MetaItem>
                                <MetaLabel>{translate("Order Date")}</MetaLabel>
                                <MetaValue>
                                    {getLocalizedDateTime({ dateTime: new Date(orderDate as Date), language })}
                                </MetaValue>
                            </MetaItem>
                        )}
                        {cart.poNumber && (
                            <MetaItem>
                                <MetaLabel>{translate("PO Number")}</MetaLabel>
                                <MetaValue>{cart.poNumber}</MetaValue>
                            </MetaItem>
                        )}
                    </MetaBlock>
                    <MetaBlock>
                        {statusDisplay && (
                            <MetaItem>
                                <MetaLabel>{translate("Status")}</MetaLabel>
                                <MetaValue>{statusDisplay}</MetaValue>
                            </MetaItem>
                        )}
                        {cart.salespersonName && (
                            <MetaItem>
                                <MetaLabel>{translate("Salesperson")}</MetaLabel>
                                <MetaValue>{cart.salespersonName}</MetaValue>
                            </MetaItem>
                        )}
                    </MetaBlock>
                </MetaRow>

                <InfoGrid>
                    <InfoColumn>
                        <InfoTitle>
                            {isPickUp ? translate("Pick Up Location") : translate("Shipping Address")}
                        </InfoTitle>
                        {isPickUp ? (
                            isUkWebsite ? (
                                <InfoLine>{siteMessage("PickUpfromWarehouse")}</InfoLine>
                            ) : (
                                <>
                                    {renderWarehouseAddress(pickUpWarehouse)}
                                    {/* Pickup must be scheduled; the address alone could suggest a customer can just walk in. */}
                                    <InfoLine>
                                        {translate(
                                            "Pickup must be scheduled in advance. Please contact Customer Support at 877.275.7847 or info@suryainc.com to arrange your pickup time.",
                                        )}
                                    </InfoLine>
                                </>
                            )
                        ) : (
                            renderAddress(shipTo)
                        )}
                    </InfoColumn>
                    {(ukShippingMethod || carrierDescription || serviceDescription) && (
                        <InfoColumn>
                            <InfoTitle>{translate("Shipping Method")}</InfoTitle>
                            {ukShippingMethod && <InfoLine>{ukShippingMethod}</InfoLine>}
                            {!isUkWebsite && carrierDescription && (
                                <InfoLine>
                                    {translate("Carrier")}: {carrierDescription}
                                </InfoLine>
                            )}
                            {!isUkWebsite && serviceDescription && (
                                <InfoLine>
                                    {translate("Service")}: {serviceDescription}
                                </InfoLine>
                            )}
                        </InfoColumn>
                    )}
                    <InfoColumn>
                        <InfoTitle>{translate("Billing Address")}</InfoTitle>
                        {renderAddress(billTo)}
                        {cart.creditCardBillingAddress && (
                            <>
                                <InfoTitle style={{ marginTop: "10px" }}>
                                    {translate("Credit Card Address")}
                                </InfoTitle>
                                <AddressBlock
                                    address1={cart.creditCardBillingAddress.address1}
                                    address2={cart.creditCardBillingAddress.address2}
                                    city={cart.creditCardBillingAddress.city}
                                    stateAbbreviation={cart.creditCardBillingAddress.stateAbbreviation}
                                    postalCode={cart.creditCardBillingAddress.postalCode}
                                />
                            </>
                        )}
                    </InfoColumn>
                </InfoGrid>

                <Table>
                    <thead>
                        <tr>
                            <Th>{translate("Item")}</Th>
                            <Th align="right">{translate("Unit Price")}</Th>
                            <Th align="right">{translate("Qty")}</Th>
                            <Th align="right">{translate("Total")}</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map(line => {
                            const itemNumber =
                                line.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER] || line.erpNumber || "";
                            const productName = getLineProductName(line);
                            const showItemNumber =
                                !!itemNumber && itemNumber.trim().toLowerCase() !== productName.trim().toLowerCase();
                            const selections = getVisibleMgbwSelections(line.properties);
                            const sectionOptions = line.isFixedConfiguration ? [] : line.sectionOptions || [];
                            const variantSize = line.properties?.variantSize;
                            const isWhiteGlove = line.properties?.isWhiteGlove === "true";
                            const isRollPad = line.properties?.isRollpadService === "true";
                            const lineAvailability = getLineAvailability(line);
                            return (
                                <tr key={line.id}>
                                    <Td>
                                        <ItemCell>
                                            <ProductMedia>
                                                {line.smallImagePath && (
                                                    <ProductImg
                                                        src={line.smallImagePath}
                                                        alt={line.shortDescription}
                                                    />
                                                )}
                                                <PrintBrandBadge brandName={line.properties?.sourceBrand} />
                                            </ProductMedia>
                                            <div>
                                                <ItemName>{productName}</ItemName>
                                                {showItemNumber && (
                                                    <SpecRow>
                                                        <SpecLabel>{translate("Item #")}</SpecLabel>
                                                        <SpecValue>{itemNumber}</SpecValue>
                                                    </SpecRow>
                                                )}
                                                {variantSize && <ProductSize>{variantSize}</ProductSize>}
                                                {selections.map(entry => (
                                                    <SpecRow key={entry.OptionId}>
                                                        <SpecLabel>{entry.Name}</SpecLabel>
                                                        <SpecValue>{entry.Value}</SpecValue>
                                                    </SpecRow>
                                                ))}
                                                {sectionOptions.map(option => (
                                                    <SpecRow key={option.sectionOptionId}>
                                                        <SpecLabel>{option.sectionName}</SpecLabel>
                                                        <SpecValue>{option.optionName}</SpecValue>
                                                    </SpecRow>
                                                ))}
                                                {line.notes?.trim() && (
                                                    <SpecRow>
                                                        <SpecLabel>{translate("Side Mark")}</SpecLabel>
                                                        <SpecValue>{line.notes}</SpecValue>
                                                    </SpecRow>
                                                )}
                                                {isRollPad && <ServiceLabel>{translate("Roll Pad Service")}</ServiceLabel>}
                                                {isWhiteGlove && (
                                                    <ServiceLabel>{translate("White Glove Service")}</ServiceLabel>
                                                )}
                                            </div>
                                        </ItemCell>
                                    </Td>
                                    <Td align="right">
                                        <div>{line.pricing?.unitNetPriceDisplay ?? ""}</div>
                                        {lineAvailability && <div>{lineAvailability}</div>}
                                    </Td>
                                    <Td align="right">{line.qtyOrdered}</Td>
                                    <Td align="right">{line.pricing?.extendedUnitNetPriceDisplay ?? ""}</Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <TotalsWrapper>
                    <Totals>
                        <TotalRow>
                            <span>{translate("Subtotal")}</span>
                            <span>{cart.orderSubTotalDisplay}</span>
                        </TotalRow>
                        <TotalRow>
                            <span>{translate("Shipping & Handling")}</span>
                            <span>
                                <LocalizedCurrency amount={baseShippingAndHandling} currencySymbol={cart.currencySymbol} />
                            </span>
                        </TotalRow>
                        {whiteGloveTotal > 0 && (
                            <TotalRow>
                                <span>{translate("White Glove")}</span>
                                <span>
                                    <LocalizedCurrency amount={whiteGloveTotal} currencySymbol={cart.currencySymbol} />
                                </span>
                            </TotalRow>
                        )}
                        <TotalRow>
                            <span>{translate("Tax")}</span>
                            <span>{cart.totalTaxDisplay}</span>
                        </TotalRow>
                        <GrandTotalRow>
                            <span>{translate("Total")}</span>
                            <span>{cart.orderGrandTotalDisplay}</span>
                        </GrandTotalRow>
                        {showPaymentAllocation && (
                            <PaymentAllocation>
                                {splitTotals.chargedToday > 0 && (
                                    <PaymentAllocationRow>
                                        <span>{translate("Charged Today")}</span>
                                        <span>
                                            <LocalizedCurrency
                                                amount={splitTotals.chargedToday}
                                                currencySymbol={cart.currencySymbol}
                                            />
                                        </span>
                                    </PaymentAllocationRow>
                                )}
                                {splitTotals.onTerms > 0 && (
                                    <PaymentAllocationRow>
                                        <span>
                                            {isUkWebsite
                                                ? termsLabel || ""
                                                : `${translate("On terms")}${termsLabel ? ` · ${termsLabel}` : ""}`}
                                        </span>
                                        {!isUkWebsite && (
                                            <span>
                                                <LocalizedCurrency
                                                    amount={splitTotals.onTerms}
                                                    currencySymbol={cart.currencySymbol}
                                                />
                                            </span>
                                        )}
                                    </PaymentAllocationRow>
                                )}
                            </PaymentAllocation>
                        )}
                    </Totals>
                </TotalsWrapper>
            </PrintRoot>
        </>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default connect(mapStateToProps)(PrintOrderConfirmationView);
